import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Info, FileText, Loader2, CheckCircle, ArrowRight, X, CloudOff, Cloud } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ChecklistPage from './ChecklistPage.js';
import FormDetailsModal from './FormDetailsModal';
import { useToast } from '../Toast';
import { useTranslation } from '../hooks/useTranslation';
import apiClient from '../apiClient';
import auditService from '../auditService';
import cloudinaryService from '../cloudinaryService';
import * as qgsStore from '../qgsSubmissionStore';
import * as qgsApi from '../qgsApi';

const QGS_GATES = new Set(['QG1', 'QG_FAN', 'QG2', 'B_KANSA']);

// Category Transition Modal Component
const CategoryTransitionModal = ({
  isOpen,
  onClose,
  onContinue,
  currentCategory,
  nextCategory,
  progress,
  areAllCompleted,
  incompleteCount
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            {t('checklist.category_completed')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {currentCategory && (
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-1">{t('checklist.completed')}</h3>
              <p className="text-green-700">{currentCategory.translation}</p>
              <p className="text-sm text-green-600">{currentCategory.name}</p>
            </div>
          )}
          
          {nextCategory ? (
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-1">{t('checklist.next_category')}</h3>
              <p className="text-blue-700">{nextCategory.translation}</p>
              <p className="text-sm text-blue-600">{nextCategory.name}</p>
            </div>
          ) : (
            <div className={`text-center p-4 rounded-lg ${areAllCompleted ? 'bg-purple-50' : 'bg-yellow-50'}`}>
              <h3 className={`font-semibold mb-1 ${areAllCompleted ? 'text-purple-800' : 'text-yellow-800'}`}>
                {areAllCompleted ? t('checklist.all_complete') : t('checklist.categories_reviewed')}
              </h3>
              <p className={areAllCompleted ? 'text-purple-700' : 'text-yellow-700'}>
                {areAllCompleted
                  ? t('checklist.ready_for_summary')
                  : t('checklist.items_need_completion')
                      .replace('{0}', incompleteCount)
                      .replace('{1}', incompleteCount !== 1 ? 's' : '')
                      .replace('{2}', incompleteCount === 1 ? 's' : '')}
              </p>
              {!areAllCompleted && (
                <p className="text-xs text-yellow-600 mt-2">
                  {t('checklist.each_item_requirement')}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('checklist.progress')}</span>
              <span>{progress.completed + 1} {t('checklist.of_categories').replace('{0}', progress.total)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((progress.completed + 1)/ progress.total) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              {t('checklist.stay_here')}
            </Button>
            <Button 
              onClick={onContinue} 
              className="flex-1"
              variant={!nextCategory && !areAllCompleted ? "secondary" : "default"}
            >
              {nextCategory ? (
                <>
                  {t('checklist.continue')}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              ) : (
                areAllCompleted ? t('checklist.go_to_summary') : t('checklist.try_to_continue')
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ChecklistPageLoop Component - ENHANCED WITH CLOUDINARY SUPPORT
const ChecklistPageLoop = ({
  onNavigate,
  selectedForm,
  auditFormData,
  isLoadingFormData,
  formDataError,
  selectedCategory,
  selectedControlItem,
  navigationContext, // NEW: Atomic navigation context
  onNext,
  onPrevious,
  onAuditComplete,
  navigateToStep,
  currentAuditResponse,
  onSaveProgress,
  checklistData,  // Add this to receive resumed draft data
  onChecklistItemChange,
  onScoreUpdate,  // Callback to update parent score
  showSummaryButton = false  // NEW: Show Summary button when audit is complete
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);

  const [currentControlItemIndex, setCurrentControlItemIndex] = useState(0);
  const [isCategoryTransitionOpen, setIsCategoryTransitionOpen] = useState(false);
  const [showCategoryToast, setShowCategoryToast] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  // Initialize cumulativeAuditData with resumed draft data if available
  const [cumulativeAuditData, setCumulativeAuditData] = useState(checklistData || {});

  // Sync cumulativeAuditData when checklistData changes (e.g., when navigating back from summary)
  // QGS v2 · The parent may reconstruct `checklistData` on every render.
  // Blindly setting state here caused a feedback loop: identity change →
  // score useEffect → onScoreUpdate → parent re-render → new checklistData
  // → repeat. Compare serialized content before setting so a prop that is
  // only reference-new is ignored.
  const lastSyncedSigRef = useRef('');
  useEffect(() => {
    if (checklistData && Object.keys(checklistData).length > 0) {
      let sig;
      try { sig = JSON.stringify(checklistData); } catch { sig = ''; }
      if (sig && sig !== lastSyncedSigRef.current) {
        lastSyncedSigRef.current = sig;
        setCumulativeAuditData(checklistData);
      }
    }
  }, [checklistData]);

  // Use ref to always have access to latest cumulativeAuditData without causing re-renders
  const cumulativeAuditDataRef = useRef(cumulativeAuditData);
  useEffect(() => {
    cumulativeAuditDataRef.current = cumulativeAuditData;
  }, [cumulativeAuditData]);
  const [saveFailureDialog, setSaveFailureDialog] = useState({
    open: false,
    message: '',
    pendingAction: null
  });
  const [cloudinaryStatus, setCloudinaryStatus] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false); // Add navigation loading state
  const [isLoadingChecks, setIsLoadingChecks] = useState(false); // Add checks loading state
  const [isFetchingScore, setIsFetchingScore] = useState(false); // Prevent concurrent score fetches
  
  // Score state
  const [currentScore, setCurrentScore] = useState({ percentage: 0, achieved: 0, possible: 0 });

  // Independent category progress tracking
  const [categoryProgressMap, setCategoryProgressMap] = useState({});

  // Get current control item index for a specific category
  const getCurrentControlItemIndexForCategory = useCallback((categoryId) => {
    return categoryProgressMap[categoryId]?.currentControlItemIndex || 0;
  }, [categoryProgressMap]);

  // Update category progress
  const updateCategoryProgress = useCallback((categoryId, controlItemIndex) => {
    setCategoryProgressMap(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        currentControlItemIndex: controlItemIndex,
        lastVisited: new Date().toISOString()
      }
    }));
  }, []);

  // Use refs to prevent infinite loops
  const isInitializedRef = useRef(false);
  const lastAuditResponseIdRef = useRef(null); // Track audit response ID to reset on change
  const lastControlItemIdRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const selectedControlItemInitializedRef = useRef(false);
  const lastSelectedControlItemRef = useRef(null);
  const visitTrackedRef = useRef(new Set()); // Track which control items have been visited
  const processedNavigationTimestamps = useRef(new Set()); // Track processed atomic navigation timestamps

  // Reset initialization when audit response changes (different audit loaded)
  useEffect(() => {
    const currentResponseId = currentAuditResponse?.audit_form_response_id;
    if (currentResponseId && currentResponseId !== lastAuditResponseIdRef.current) {
      console.log('🔄 Audit response changed, resetting initialization:', {
        oldId: lastAuditResponseIdRef.current,
        newId: currentResponseId
      });
      isInitializedRef.current = false;
      lastAuditResponseIdRef.current = currentResponseId;
    }
  }, [currentAuditResponse?.audit_form_response_id]);

  // Reset control item initialization when component mounts or selectedControlItem prop changes
  useEffect(() => {
    console.log('🔄 ChecklistPageLoop: selectedControlItem prop changed, resetting initialization');
    selectedControlItemInitializedRef.current = false;
    lastSelectedControlItemRef.current = null;
  }, [selectedControlItem?.id]); // Reset when the ID changes

  // ATOMIC NAVIGATION: Moved to after allCategories declaration

  // Check Cloudinary configuration on mount - ONLY ONCE
  useEffect(() => {
    const isConfigured = cloudinaryService.isConfigured();
    setCloudinaryStatus(isConfigured);
    
    if (!isConfigured) {
      console.warn('⚠️ Cloudinary is not configured for image uploads');
      // Only show toast once on initial mount, not on every render
      // Remove the toast here to prevent spam
    } else {
      console.log('✅ Cloudinary is configured and ready for image uploads');
    }
  }, []); // Empty dependency array - runs only once on mount

  // Generate all categories from auditFormData - MEMOIZED to prevent infinite loops
  const allCategories = useMemo(() => {
    if (!auditFormData?.data?.form_structure) {
      return [
        { id: 1, name: "DOCUMENTAZIONE", translation: "Documentation", originalCategory: null },
        { id: 2, name: "ETICHETTE", translation: "Labels", originalCategory: null }
      ];
    }

    return auditFormData.data.form_structure.map((category, index) => {
      const translation = category.categoryData?.category_code_it ||
                         category.title.split(' / ')[1] || "";

      return {
        id: category.id || index + 1,
        name: category.categoryData?.category_code || category.title.split(' / ')[0] || category.title,
        translation: translation,
        originalCategory: category
      };
    });
  }, [auditFormData?.data?.form_structure]);

  const currentCategory = allCategories[currentCategoryIndex];

  // ATOMIC NAVIGATION: Single initialization from navigationContext (after allCategories declaration)
  useEffect(() => {
    if (navigationContext?.timestamp && navigationContext.category && navigationContext.controlItem && allCategories.length > 0) {
      // CRITICAL: Only process each timestamp once to avoid interfering with manual navigation
      if (processedNavigationTimestamps.current.has(navigationContext.timestamp)) {
        console.log('🔄 ATOMIC NAVIGATION: Timestamp already processed, skipping:', navigationContext.timestamp);
        return;
      }

      console.log('🎯 ATOMIC NAVIGATION: Processing navigation context:', {
        timestamp: navigationContext.timestamp,
        categoryName: navigationContext.category.name,
        controlItemName: navigationContext.controlItem.name,
        categoryId: navigationContext.category.originalCategory?.categoryData?.category_id,
        controlItemId: navigationContext.controlItem.originalControlItem?.control_item_id
      });

      // Mark timestamp as processed
      processedNavigationTimestamps.current.add(navigationContext.timestamp);

      // STEP 1: Set category index
      const categoryIndex = allCategories.findIndex(cat =>
        cat.id === navigationContext.category.id ||
        cat.originalCategory?.categoryData?.category_id === navigationContext.category.originalCategory?.categoryData?.category_id
      );

      if (categoryIndex !== -1) {
        console.log('✅ Setting category index to:', categoryIndex);
        setCurrentCategoryIndex(categoryIndex);

        // STEP 2: Find control items for this specific category
        const categoryId = navigationContext.category.originalCategory?.categoryData?.category_id;
        if (categoryId && auditFormData?.hierarchy?.control_items) {
          const categoryControlItems = auditFormData.hierarchy.control_items.filter(
            item => item.category_id === categoryId
          );

          // STEP 3: Find control item index in this category
          const controlItemIndex = categoryControlItems.findIndex(item =>
            item.control_item_id === navigationContext.controlItem.originalControlItem?.control_item_id ||
            item.control_item_code === navigationContext.controlItem.name
          );

          if (controlItemIndex !== -1) {
            console.log('✅ Setting control item index to:', controlItemIndex);
            setCurrentControlItemIndex(controlItemIndex);
            console.log('🎯 ATOMIC NAVIGATION SUCCESS:', { categoryIndex, controlItemIndex });
          } else {
            console.log('❌ Control item not found in category, available items:',
              categoryControlItems.map(item => ({ id: item.control_item_id, code: item.control_item_code })));
          }
        }
      } else {
        console.log('❌ Category not found, available categories:',
          allCategories.map(cat => ({ id: cat.id, name: cat.name })));
      }
    }
  }, [navigationContext?.timestamp, allCategories, auditFormData?.hierarchy?.control_items]); // Dependencies for atomic navigation

  // Get control items for current category
  const getCurrentCategoryControlItems = useCallback(() => {
    if (!auditFormData?.hierarchy?.control_items || !currentCategory) {
      return [];
    }

    const categoryId = currentCategory.originalCategory?.categoryData?.category_id;
    if (!categoryId) {
      return [];
    }

    return auditFormData.hierarchy.control_items.filter(
      controlItem => controlItem.category_id === categoryId
    );
  }, [auditFormData?.hierarchy?.control_items, currentCategory]);

  const currentCategoryControlItems = getCurrentCategoryControlItems();
  const currentControlItem = currentCategoryControlItems[currentControlItemIndex];

  // Load saved data from backend with fresh data retrieval
  useEffect(() => {
    // Skip if already initialized
    if (isInitializedRef.current) {
      console.log('✅ ChecklistPageLoop already initialized, skipping backend load');
      return;
    }

    const loadSavedDataFromBackend = async () => {
      if (!currentAuditResponse?.audit_form_response_id) return;

      // If we already have checklistData from props (resumed draft), use it instead of loading from backend
      if (checklistData && Object.keys(checklistData).length > 0) {
        console.log('📊 Using checklistData from props (resumed draft), skipping backend load:', {
          categoriesCount: Object.keys(checklistData).length
        });
        isInitializedRef.current = true;
        return;
      }

      console.log('🔄 Loading saved data from backend...');
      try {
        const result = await auditService.getAuditResponse(
          currentAuditResponse.audit_form_response_id,
          { useCache: false }
        );

        if (result.success && result.auditResponse.raw_response_json?.cumulativeAuditData) {
          const savedData = result.auditResponse.raw_response_json;

          console.log('✅ Loaded data from backend:', {
            categoriesCount: Object.keys(savedData.cumulativeAuditData || {}).length
          });

          setCumulativeAuditData(savedData.cumulativeAuditData || {});

          if (savedData.currentCategoryIndex !== undefined) {
            setCurrentCategoryIndex(savedData.currentCategoryIndex);
          }
          if (savedData.currentControlItemIndex !== undefined) {
            setCurrentControlItemIndex(savedData.currentControlItemIndex);
          }

          isInitializedRef.current = true;
        }
      } catch (error) {
        console.error('❌ Error loading saved data from backend:', error);
        toast.error('Failed to load saved audit data');
      }
    };

    if (currentAuditResponse) {
      loadSavedDataFromBackend();
    }
  }, [currentAuditResponse?.audit_form_response_id, checklistData, toast]); // Removed cumulativeAuditData from deps to prevent loops

  // Initialize category index if not loaded from saved data
  useEffect(() => {
    // First check if we should restore from saved position (for resumed drafts)
    if (checklistData && currentAuditResponse?.raw_response_json && !isInitializedRef.current) {
      const savedData = currentAuditResponse.raw_response_json;
      if (savedData.currentCategoryIndex !== undefined && savedData.currentControlItemIndex !== undefined) {
        setCurrentCategoryIndex(savedData.currentCategoryIndex);
        setCurrentControlItemIndex(savedData.currentControlItemIndex);
        isInitializedRef.current = true;
        return;
      }
    }
    
    // Otherwise initialize from selected category - BUT ONLY if we're at initial state
    if (!isInitializedRef.current && selectedCategory && allCategories.length > 0 && currentCategoryIndex === 0) {
      const categoryIndex = allCategories.findIndex(cat =>
        cat.id === selectedCategory.id ||
        cat.name === selectedCategory.name
      );
      if (categoryIndex !== -1) {
        console.log('🔄 Legacy initialization: Setting category index to:', categoryIndex);
        setCurrentCategoryIndex(categoryIndex);
      }
      isInitializedRef.current = true;
    } else if (!isInitializedRef.current && currentCategoryIndex > 0) {
      console.log('🔄 Legacy initialization: Skipping - already navigated to category:', currentCategoryIndex);
      isInitializedRef.current = true;
    }
  }, [selectedCategory, allCategories, checklistData, currentAuditResponse, currentCategoryIndex]);

  // Store category reference to detect category changes
  const lastCategoryIdRef = useRef(null);

  // Initialize control item index - PRIORITIZE CATEGORY PROGRESS, then selectedControlItem
  useEffect(() => {
    // Reset initialization flag if selectedControlItem changes or current category changes
    const currentCategoryId = currentCategory?.originalCategory?.categoryData?.category_id;

    // Reset if category changed OR selectedControlItem changed
    if ((currentCategoryId && lastCategoryIdRef.current !== currentCategoryId) ||
        (selectedControlItem && lastSelectedControlItemRef.current !== selectedControlItem.id) ||
        !selectedControlItemInitializedRef.current) {

      console.log('🔄 Control item/category changed or not initialized:', {
        previousCategoryId: lastCategoryIdRef.current,
        currentCategoryId: currentCategoryId,
        previousItemId: lastSelectedControlItemRef.current,
        newItemId: selectedControlItem?.id,
        wasInitialized: selectedControlItemInitializedRef.current
      });

      selectedControlItemInitializedRef.current = false;
      lastCategoryIdRef.current = currentCategoryId;
      if (selectedControlItem) {
        lastSelectedControlItemRef.current = selectedControlItem.id;
      }
    }

    if (!selectedControlItemInitializedRef.current &&
        currentCategoryControlItems.length > 0 &&
        currentCategoryId) {

      console.log('🎯 Initializing control item index for category:', currentCategoryId);

      // STEP 1: If user clicked a specific control item, prioritize that over saved progress
      // STEP 2: Otherwise, check for saved progress (for automatic category switching)
      if (selectedControlItem) {
        console.log('🔍 No saved progress, checking selectedControlItem:', {
          selectedItem: selectedControlItem,
          selectedId: selectedControlItem.id,
          selectedName: selectedControlItem.name,
          selectedTranslation: selectedControlItem.translation,
          originalControlItemId: selectedControlItem.originalControlItem?.control_item_id,
          availableItems: currentCategoryControlItems.length,
          currentIndex: currentControlItemIndex
        });

        // Debug: Log available control items for comparison
        console.log('🔍 Available control items in current category:',
          currentCategoryControlItems.map(item => ({
            control_item_id: item.control_item_id,
            control_item_code: item.control_item_code,
            control_item_code_it: item.control_item_code_it
          }))
        );

        // Simple, precise match - both use same data source so IDs should match
        const controlItemIndex = currentCategoryControlItems.findIndex(item => {
          const match = item.control_item_id === selectedControlItem.originalControlItem?.control_item_id;
          console.log(`🔍 Comparing item ${item.control_item_id} with selected ${selectedControlItem.originalControlItem?.control_item_id}: ${match}`);
          return match;
        });

        if (controlItemIndex !== -1) {
          console.log('✅ Found control item at index:', controlItemIndex, 'for item:', selectedControlItem.name);
          setCurrentControlItemIndex(controlItemIndex);
          // Update category progress when navigating to specific item
          updateCategoryProgress(currentCategoryId, controlItemIndex);
        } else {
          console.log('❌ Control item not found, using saved progress or defaulting to index 0');
          const savedProgress = getCurrentControlItemIndexForCategory(currentCategoryId);
          if (savedProgress > 0) {
            console.log('📍 Using saved progress as fallback:', savedProgress);
            setCurrentControlItemIndex(savedProgress);
          } else {
            console.log('📍 Defaulting to index 0');
            setCurrentControlItemIndex(0);
            updateCategoryProgress(currentCategoryId, 0);
          }
        }
      } else {
        // No selectedControlItem - use saved progress or start from beginning
        const savedProgress = getCurrentControlItemIndexForCategory(currentCategoryId);
        if (savedProgress > 0) {
          console.log('📍 No selectedControlItem, using saved progress:', savedProgress);
          setCurrentControlItemIndex(savedProgress);
        } else {
          console.log('📍 No selectedControlItem, starting from index 0');
          setCurrentControlItemIndex(0);
          updateCategoryProgress(currentCategoryId, 0);
        }
      }

      selectedControlItemInitializedRef.current = true;
    }
  }, [selectedControlItem, currentCategoryControlItems, getCurrentControlItemIndexForCategory, updateCategoryProgress, currentCategory]);

  // Load checklist items from backend data with saved state including Cloudinary images
  useEffect(() => {
    const loadChecklistItemsFromBackend = () => {
      const currentControlItemId = currentControlItem?.control_item_id;
      
      if (!currentControlItemId || currentControlItemId === lastControlItemIdRef.current) {
        return;
      }
      
      setIsLoadingChecks(true);
      console.log('🔄 Loading checks for control item:', currentControlItemId);
      
      console.log('🔄 Loading checklist items from backend data...');
      lastControlItemIdRef.current = currentControlItemId;
      
      if (currentControlItem && auditFormData?.hierarchy?.checks) {
        const freshChecks = auditFormData.hierarchy.checks.filter(
          check => check.control_item_id === currentControlItem.control_item_id
        );
        
        console.log(`📋 Found ${freshChecks.length} checks for control item ${currentControlItemId}`);
        
        
        const categoryId = currentCategory?.originalCategory?.categoryData?.category_id;
        const controlItemId = currentControlItem.control_item_id;
        
        const newItems = freshChecks.map(check => {
          const savedCheckData = cumulativeAuditData[categoryId]?.controlItems?.[controlItemId]?.checks?.[check.check_id];
          
          // Process saved images to ensure they have proper Cloudinary URLs
          let processedImages = [];
          if (savedCheckData?.images && Array.isArray(savedCheckData.images)) {
            processedImages = savedCheckData.images.map(img => {
              // Ensure image has all necessary properties
              return {
                ...img,
                id: img.id || Math.random().toString(36).substr(2, 9),
                url: img.url || img.cloudinaryUrl || '',
                thumbnailUrl: img.thumbnailUrl || (img.url ? cloudinaryService.getThumbnailUrl(img.url, 200) : ''),
                alt: img.alt || `Image for ${check.check_code}`,
                uploadStatus: img.uploadStatus || 'success',
                cloudinaryId: img.cloudinaryId || img.publicId || '',
                publicId: img.publicId || '',
                cloudinaryData: img.cloudinaryData || null
              };
            });
          }
          
          const item = {
            id: check.check_id.toString(),
            label: check.check_code,
            labelItalian: check.check_code_it,
            description: check.reference_standard || `Deduction: ${check.deduction_points} points`,
            isChecked: savedCheckData?.isChecked || false,
            quantity: savedCheckData?.quantity || 1,
            productionPhase: savedCheckData?.productionPhase || 'Phase',
            isFixedBeforeShipment: false, // Always default to false (field hidden in UI)
            images: processedImages,
            comment: savedCheckData?.comment || '',
            deductionPoints: check.deduction_points,
            measurementUnit: check.measurement_unit,
            displayOrder: check.display_order,
            requiresQuantity: check.qnty,
            requiresAttachment: check.requires_attachment,
            requiresComment: check.comments,
            requiresProductionPhase: check.production_phase,
            requiresFixedBeforeShipment: false, // Always false (field hidden in UI)
            // Per-check M2M allow-lists from the QGS backend. When empty,
            // ChecklistPage's `scopedLookupsFor` treats it as "no restriction"
            // and shows the full master — so the editor MUST attach these
            // for the tri-state dropdowns to narrow to the form's selection.
            ko_allowed_defect_types: check.ko_allowed_defect_types || [],
            ko_allowed_defect_locations: check.ko_allowed_defect_locations || [],
            rework_allowed_types: check.rework_allowed_types || [],
            originalCheck: check,
            loadedAt: new Date().toISOString(),
            lastModified: savedCheckData?.lastUpdated || null
          };
          
          
          return item;
        });
        
        setChecklistItems(newItems);
        setIsLoadingChecks(false);
        
        const totalImages = newItems.reduce((sum, item) => sum + (item.images?.length || 0), 0);
        console.log(`✅ Loaded ${newItems.length} checklist items with ${newItems.filter(item => item.isChecked).length} marked as failed and ${totalImages} total images`);
      } else {
        setChecklistItems([]);
        setIsLoadingChecks(false);
      }
    };
    
    loadChecklistItemsFromBackend();
  }, [currentControlItem?.control_item_id, currentCategory?.originalCategory?.categoryData?.category_id, auditFormData?.hierarchy?.checks, cumulativeAuditData]);


  // Progress tracking utility functions
  const calculateControlItemStatus = (controlItemData) => {
    const allChecks = Object.values(controlItemData.checks || {});
    const completedChecks = allChecks.filter(check => check.isCompleted === true).length;
    const totalChecks = allChecks.length;
    const hasBeenVisited = controlItemData.hasBeenVisited || false;


    // FIXED: Control item is completed when at least one check is selected (Group A or Group B)
    if (totalChecks > 0 && completedChecks > 0) return 'Completed';
    if (hasBeenVisited) return 'Draft mode';
    return 'Not yet started';
  };

  const calculateCategoryStatus = (categoryData) => {
    const totalControlItems = categoryData.totalControlItems || 0;
    const completedControlItems = categoryData.completedControlItems || 0;
    
    if (completedControlItems === 0) return 'Not Started';
    if (completedControlItems < totalControlItems) return 'In Progress';
    return 'Completed';
  };

  const updateControlItemStats = (controlItemData) => {
    const allChecks = Object.values(controlItemData.checks || {});
    const selectedChecks = allChecks.filter(check => check.isChecked === true);
    const completedChecks = allChecks.filter(check => check.isCompleted === true);

    controlItemData.totalChecks = allChecks.length;
    controlItemData.selectedChecks = selectedChecks.length;
    controlItemData.completedChecks = completedChecks.length;
    controlItemData.controlItemStatus = calculateControlItemStatus(controlItemData);


    return controlItemData;
  };

  const updateCategoryStats = (categoryData) => {
    const allControlItems = Object.values(categoryData.controlItems || {});
    categoryData.totalControlItems = allControlItems.length;
    categoryData.completedControlItems = allControlItems.filter(ci => ci.controlItemStatus === 'Completed').length;
    categoryData.categoryStatus = calculateCategoryStatus(categoryData);
    
    return categoryData;
  };

  // Validation function to check if all control items are completed
  const areAllControlItemsCompleted = useCallback(() => {
    // CRITICAL FIX: Check against the COMPLETE form hierarchy, not just visited items
    if (!auditFormData?.hierarchy?.control_items || !Array.isArray(auditFormData.hierarchy.control_items)) {
      console.warn('⚠️ No form hierarchy available for validation');
      return false;
    }

    // Get all control items from the complete form definition
    const allControlItems = auditFormData.hierarchy.control_items;

    console.log(`🔍 Validating completion: ${allControlItems.length} total control items in form`);

    // Check each control item from the hierarchy
    for (const controlItem of allControlItems) {
      const categoryId = controlItem.category_id;
      const controlItemId = controlItem.control_item_id;

      // Check if this control item exists in cumulativeAuditData (has been visited and completed)
      const visitedControlItem = cumulativeAuditData[categoryId]?.controlItems?.[controlItemId];

      if (!visitedControlItem) {
        // Control item was never visited - not completed
        console.log(`❌ Control item not visited: ${controlItem.control_item_code} (Category: ${categoryId})`);
        return false;
      }

      // Update the control item stats to ensure accurate status
      const updatedControlItem = updateControlItemStats({...visitedControlItem});

      // A control item is completed if it has at least one selected check
      if (updatedControlItem.controlItemStatus !== 'Completed') {
        console.log(`❌ Control item incomplete: ${controlItem.control_item_code} (Status: ${updatedControlItem.controlItemStatus})`);
        return false;
      }
    }

    console.log('✅ All control items completed!');
    return true;
  }, [cumulativeAuditData, auditFormData?.hierarchy?.control_items]);

  // Get list of incomplete control items for user feedback
  const getIncompleteControlItems = useCallback(() => {
    const incompleteItems = [];

    // CRITICAL FIX: Check against the COMPLETE form hierarchy, not just visited items
    if (!auditFormData?.hierarchy?.control_items || !Array.isArray(auditFormData.hierarchy.control_items)) {
      console.warn('⚠️ No form hierarchy available for getting incomplete items');
      return incompleteItems;
    }

    // Get all categories for lookup
    const categoriesMap = {};
    if (auditFormData?.data?.form_structure) {
      auditFormData.data.form_structure.forEach(cat => {
        const categoryId = cat.categoryData?.category_id;
        if (categoryId) {
          categoriesMap[categoryId] = {
            name: cat.categoryData?.category_code || cat.title,
            translation: cat.categoryData?.category_code_it || cat.title
          };
        }
      });
    }

    // Check each control item from the complete hierarchy
    const allControlItems = auditFormData.hierarchy.control_items;

    for (const controlItem of allControlItems) {
      const categoryId = controlItem.category_id;
      const controlItemId = controlItem.control_item_id;

      // Check if this control item exists in cumulativeAuditData
      const visitedControlItem = cumulativeAuditData[categoryId]?.controlItems?.[controlItemId];

      if (!visitedControlItem) {
        // Control item was never visited - mark as incomplete
        incompleteItems.push({
          categoryName: categoriesMap[categoryId]?.translation || categoriesMap[categoryId]?.name || `Category ${categoryId}`,
          controlItemName: controlItem.control_item_code_it || controlItem.control_item_code,
          status: 'Not yet started'
        });
        continue;
      }

      // Update the control item stats to ensure accurate status
      const updatedControlItem = updateControlItemStats({...visitedControlItem});

      if (updatedControlItem.controlItemStatus !== 'Completed') {
        incompleteItems.push({
          categoryName: categoriesMap[categoryId]?.translation || categoriesMap[categoryId]?.name || `Category ${categoryId}`,
          controlItemName: controlItem.control_item_code_it || controlItem.control_item_code,
          status: updatedControlItem.controlItemStatus
        });
      }
    }

    return incompleteItems;
  }, [cumulativeAuditData, auditFormData?.hierarchy?.control_items, auditFormData?.data?.form_structure]);

  // Initialize category progress from saved data
  useEffect(() => {
    if (currentAuditResponse?.raw_response_json?.categoryProgressMap) {
      setCategoryProgressMap(currentAuditResponse.raw_response_json.categoryProgressMap);
    }
  }, [currentAuditResponse?.raw_response_json?.categoryProgressMap]);

  // Backend score handling - replaces local calculation for consistency
  const updateScoreFromBackend = useCallback((scoreInfo) => {
    if (scoreInfo) {
      const newScore = {
        percentage: Math.round(scoreInfo.current_score || 0),
        achieved: scoreInfo.total_achieved || 0,
        possible: scoreInfo.total_possible || 0
      };

      // Update local state immediately
      setCurrentScore(newScore);

      if (onScoreUpdate) {
        onScoreUpdate(newScore);
      }
    }
  }, [onScoreUpdate]);

  // Fetch complete cumulative audit score (not just current control item)
  const fetchCumulativeAuditScore = useCallback(async () => {
    if (!currentAuditResponse?.audit_form_response_id || isFetchingScore) {
      return;
    }

    setIsFetchingScore(true);

    // Use ref to get the latest cumulativeAuditData without causing dependency issues
    const completeAuditData = cumulativeAuditDataRef.current && Object.keys(cumulativeAuditDataRef.current).length > 0
      ? cumulativeAuditDataRef.current
      : {};

    try {
      const result = await auditService.calculateRealtimeScore(
        currentAuditResponse.audit_form_response_id,
        completeAuditData
      );

      if (result.success && result.score_data) {
        const scoreInfo = {
          current_score: result.score_data.score_percentage,
          total_achieved: result.score_data.total_points_achieved,
          total_possible: result.score_data.total_points_possible
        };
        updateScoreFromBackend(scoreInfo);
      }
    } catch (error) {
      console.error('❌ Failed to fetch cumulative audit score:', error);
    } finally {
      setIsFetchingScore(false);
    }
  }, [currentAuditResponse?.audit_form_response_id, isFetchingScore, updateScoreFromBackend]);

  // Fetch cumulative score ONLY on component mount
  useEffect(() => {
    if (currentAuditResponse?.audit_form_response_id) {
      fetchCumulativeAuditScore();
    }
  }, [currentAuditResponse?.audit_form_response_id, fetchCumulativeAuditScore]); // Only when audit response changes

  // Real-time score update when checks are modified with debounce
  useEffect(() => {
    if (Object.keys(cumulativeAuditData).length === 0) return; // Skip if no data

    // Debounce to prevent too many calls
    const scoreUpdateTimeout = setTimeout(() => {
      fetchCumulativeAuditScore();
    }, 100); // 100ms debounce to batch rapid changes

    return () => clearTimeout(scoreUpdateTimeout);
  }, [cumulativeAuditData]); // Update when actual data changes (fetchCumulativeAuditScore is stable now)

  // Auto-save with immediate backend sync on navigation
  const handleNavigationSave = useCallback(async () => {
    if (!currentAuditResponse || !cumulativeAuditData || Object.keys(cumulativeAuditData).length === 0) {
      return { success: true, message: 'No data to save' };
    }

    console.log('🚀 Saving data before navigation...');
    
    try {
      // Check if there are any Cloudinary images
      let hasCloudinaryImages = false;
      let totalCloudinaryImages = 0;
      
      Object.values(cumulativeAuditData).forEach(category => {
        Object.values(category.controlItems || {}).forEach(controlItem => {
          Object.values(controlItem.checks || {}).forEach(check => {
            if (check.images && Array.isArray(check.images)) {
              check.images.forEach(img => {
                if (img.cloudinaryId || img.publicId || (img.url && img.url.includes('cloudinary'))) {
                  hasCloudinaryImages = true;
                  totalCloudinaryImages++;
                }
              });
            }
          });
        });
      });
      
      const progressData = {
        cumulativeAuditData,
        currentCategoryIndex,
        currentControlItemIndex,
        categoryProgressMap, // Add independent category progress tracking
        currentContext: {
          categoryId: currentCategory?.originalCategory?.categoryData?.category_id,
          categoryName: currentCategory?.name,
          categoryTranslation: currentCategory?.translation,
          controlItemId: currentControlItem?.control_item_id,
          controlItemName: currentControlItem?.control_item_code,
          controlItemTranslation: currentControlItem?.control_item_code_it
        },
        hasCloudinaryImages,
        totalCloudinaryImages,
        cloudinaryEnabled: cloudinaryStatus,
        lastUpdated: new Date().toISOString()
      };

      console.log('🔍 NAVIGATION SAVE DEBUG - Data being saved:', {
        cumulativeDataKeys: Object.keys(cumulativeAuditData),
        cumulativeDataCount: Object.keys(cumulativeAuditData).length,
        sampleCategory: Object.values(cumulativeAuditData)[0],
        progressDataKeys: Object.keys(progressData),
        fullCumulativeData: cumulativeAuditData,
        progressDataCumulativeData: progressData.cumulativeAuditData
      });

      const result = await auditService.updateAuditResponse(
        currentAuditResponse.audit_form_response_id,
        { raw_response_json: progressData },
        { immediate: true, calculateScore: true }
      );

      if (result.success) {
        console.log('✅ Navigation save successful');
        if (hasCloudinaryImages) {
          console.log(`☁️ Saved ${totalCloudinaryImages} Cloudinary images`);
        }
        if (result.scoreInfo) {
          console.log(`📊 Current score: ${result.scoreInfo.current_score}%`);
          // Use the score from save result directly
          updateScoreFromBackend(result.scoreInfo);
        }
        return { success: true, message: 'Data saved successfully' };
      } else {
        console.error('❌ Navigation save failed:', result.error);
        return { success: false, message: result.error || 'Failed to save data' };
      }
    } catch (error) {
      console.error('❌ Error during navigation save:', error);
      return { success: false, message: error.message || 'Error during save operation' };
    }
  }, [currentAuditResponse, cumulativeAuditData, currentCategoryIndex, currentControlItemIndex, currentCategory, currentControlItem, cloudinaryStatus]); // Removed fetchCumulativeAuditScore

  // Helper function to handle save failures
  const handleSaveFailure = useCallback((message, pendingAction) => {
    setSaveFailureDialog({
      open: true,
      message: message || 'Unable to save audit data. Cannot proceed with navigation.',
      pendingAction
    });
  }, []);

  // Force save function for retry
  const handleForceSave = useCallback(async () => {
    try {
      setIsSaving(true);
      const result = await handleNavigationSave();

      if (result && result.success) {
        // Save successful, execute pending action
        const { pendingAction } = saveFailureDialog;
        setSaveFailureDialog({ open: false, message: '', pendingAction: null });

        if (pendingAction) {
          pendingAction();
        }
        toast.success('Data saved successfully');
      } else {
        toast.error(result?.message || 'Force save failed. Please try again.');
      }
    } catch (error) {
      toast.error('Force save failed: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  }, [handleNavigationSave, saveFailureDialog, toast]);

  // Wrapper for navigation that includes save check
  const handleSafeNavigate = useCallback(async (destination) => {
    if (isNavigating || isSaving) {
      console.log('⏳ Navigation in progress, please wait...');
      return;
    }

    setIsNavigating(true);

    try {
      // Save current data before navigating
      const saveResult = await handleNavigationSave();

      if (!saveResult || !saveResult.success) {
        // Save failed, show dialog
        handleSaveFailure(
          saveResult?.message || `Unable to save audit data. Cannot navigate to ${destination}.`,
          () => {
            // After successful force save, navigate to destination
            if (onNavigate) {
              onNavigate(destination);
            }
          }
        );
        return;
      }

      // Save successful, proceed with navigation
      if (onNavigate) {
        onNavigate(destination);
      }
    } finally {
      setIsNavigating(false);
    }
  }, [handleNavigationSave, handleSaveFailure, onNavigate, isNavigating, isSaving]);

  // Real-time data sync with debouncing
  const debouncedSave = useCallback(async (dataToSave) => {
    console.log('🔥 DEBOUNCED SAVE CALLED with data:', {
      dataToSaveKeys: dataToSave ? Object.keys(dataToSave) : null,
      dataStructure: dataToSave
    });

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      console.log('🔥 DEBOUNCED SAVE TIMEOUT EXECUTED');
      if (!currentAuditResponse || isSaving) return;
      
      setIsSaving(true);
      try {
        // Count Cloudinary images for tracking
        let cloudinaryImageCount = 0;
        Object.values(dataToSave).forEach(category => {
          Object.values(category.controlItems || {}).forEach(controlItem => {
            Object.values(controlItem.checks || {}).forEach(check => {
              if (check.images && Array.isArray(check.images)) {
                cloudinaryImageCount += check.images.filter(img => 
                  img.cloudinaryId || img.publicId || (img.url && img.url.includes('cloudinary'))
                ).length;
              }
            });
          });
        });

        const progressData = {
          cumulativeAuditData: dataToSave,
          currentCategoryIndex,
          currentControlItemIndex,
          categoryProgressMap, // Add independent category progress tracking
          currentContext: {
            categoryId: currentCategory?.originalCategory?.categoryData?.category_id,
            categoryName: currentCategory?.name,
            categoryTranslation: currentCategory?.translation,
            controlItemId: currentControlItem?.control_item_id,
            controlItemName: currentControlItem?.control_item_code,
            controlItemTranslation: currentControlItem?.control_item_code_it
          },
          cloudinaryImageCount,
          cloudinaryEnabled: cloudinaryStatus,
          lastUpdated: new Date().toISOString()
        };

        const result = await auditService.updateAuditResponse(
          currentAuditResponse.audit_form_response_id,
          { raw_response_json: progressData },
          { immediate: false, calculateScore: true }
        );

        if (result.success && !result.queued) {
          console.log('✅ Real-time save successful');
          if (cloudinaryImageCount > 0) {
            console.log(`☁️ ${cloudinaryImageCount} Cloudinary images saved`);
          }
          if (result.scoreInfo) {
            console.log(`📊 Updated score: ${result.scoreInfo.current_score}%`);
            // Use the score from save result directly
            updateScoreFromBackend(result.scoreInfo);
          }

          // CRITICAL DEBUG: Let's see what exactly gets saved
          console.log('🔍 CRITICAL DEBUG - Data being saved:', {
            progressDataStructure: Object.keys(progressData),
            cumulativeAuditDataStructure: progressData.cumulativeAuditData ? Object.keys(progressData.cumulativeAuditData) : null,
            sampleCategoryData: progressData.cumulativeAuditData ? Object.values(progressData.cumulativeAuditData)[0] : null
          });

          console.log('🔍 CRITICAL DEBUG - Save result:', {
            resultStructure: result ? Object.keys(result) : null,
            hasAuditResponse: !!result.auditResponse,
            auditResponseStructure: result.auditResponse ? Object.keys(result.auditResponse) : null
          });

          // CRITICAL FIX: Update the App.js state with the complete audit response
          if (onSaveProgress && result.auditResponse) {
              onSaveProgress(result.auditResponse); // Pass the complete updated audit response
          } else if (onSaveProgress) {
            // Fallback to old behavior if no auditResponse in result
            onSaveProgress(progressData);
          }
        } else if (result.queued) {
          console.log('📝 Save queued for batch processing');

          // CRITICAL FIX: Even for queued saves, we need to update the App.js state
          // with the current data so ControlItemsViewPage can see the latest status
          if (onSaveProgress) {
            console.log('🔄 Updating App.js state for queued save...');
            onSaveProgress({ cumulativeAuditData: dataToSave });
          }
        }
      } catch (error) {
        console.error('❌ Error in real-time save:', error);
        toast.error('Failed to save progress. Your data may not be preserved.');
      } finally {
        setIsSaving(false);
      }
    }, 500); // Faster debounce for better responsiveness
  }, [currentAuditResponse, currentCategoryIndex, currentControlItemIndex, currentCategory, currentControlItem, isSaving, onSaveProgress, cloudinaryStatus, toast]);

  // Track control item visits - using separate useEffect with simpler dependencies
  useEffect(() => {
    console.log('🔄 Visit tracking useEffect triggered:', {
      categoryId: currentCategory?.originalCategory?.categoryData?.category_id,
      controlItemId: currentControlItem?.control_item_id,
      checklistItemsLength: checklistItems.length,
      currentControlItemName: currentControlItem?.control_item_code
    });
    
    const categoryId = currentCategory?.originalCategory?.categoryData?.category_id;
    const controlItemId = currentControlItem?.control_item_id;
    const visitKey = `${categoryId}-${controlItemId}`;
    
    // Only track if we have both IDs, checklist items are loaded, and haven't tracked this combo yet
    if (categoryId && controlItemId && checklistItems.length > 0 && !visitTrackedRef.current.has(visitKey)) {
      console.log(`📍 Tracking visit for control item: ${currentControlItem?.control_item_code}`);
      
      // Mark as tracked immediately to prevent duplicates
      visitTrackedRef.current.add(visitKey);
      
      // Update cumulative data in a separate effect
      setCumulativeAuditData(prevData => {
        const newData = { ...prevData };
        
        // Initialize category if not exists
        if (!newData[categoryId]) {
          newData[categoryId] = {
            categoryName: currentCategory.name,
            categoryTranslation: currentCategory.translation,
            totalControlItems: 0,
            completedControlItems: 0,
            categoryStatus: 'Not Started',
            controlItems: {}
          };
        }
        
        // Initialize control item if not exists
        if (!newData[categoryId].controlItems[controlItemId]) {
          newData[categoryId].controlItems[controlItemId] = {
            // Enhanced control item information
            controlItemName: currentControlItem.control_item_code,
            controlItemTranslation: currentControlItem.control_item_code_it,
            controlItemCode: currentControlItem.control_item_code,
            controlItemId: currentControlItem.control_item_id,
            // Complete original control item reference
            originalControlItem: currentControlItem,
            // Visit tracking
            totalChecks: checklistItems.length,
            selectedChecks: 0,
            hasBeenVisited: true,
            visitedAt: new Date().toISOString(),
            controlItemStatus: 'Draft mode', // Set to draft mode when visited
            checks: {}
          };
          
          // Update statistics
          updateControlItemStats(newData[categoryId].controlItems[controlItemId]);
          updateCategoryStats(newData[categoryId]);
          
          console.log(`✅ Control item marked as visited: ${currentControlItem.control_item_code}`);
          
          // Save after a short delay to prevent conflicts
          setTimeout(() => {
            if (debouncedSave && typeof debouncedSave === 'function') {
              debouncedSave(newData);
            }
          }, 100);
        }
        
        return newData;
      });
    }
  }, [currentCategory?.originalCategory?.categoryData?.category_id, currentControlItem?.control_item_id, checklistItems.length]); // Use primitive values only

  // Item change handler with immediate UI update and Cloudinary image support
  const handleItemChange = useCallback((updatedItem) => {
    console.log(`🔥 IMMEDIATE DEBUG - Item changed: ${updatedItem.id} (checked: ${updatedItem.isChecked})`);
    console.log(`🔥 IMMEDIATE DEBUG - Will call debouncedSave?`, typeof debouncedSave);
    
    // Remove optimistic update - let backend handle it for accuracy
    
    // Count Cloudinary images in the updated item
    const cloudinaryImagesInItem = (updatedItem.images || []).filter(img => 
      img.cloudinaryId || img.publicId || (img.url && img.url.includes('cloudinary'))
    ).length;
    
    if (cloudinaryImagesInItem > 0) {
      console.log(`☁️ Item has ${cloudinaryImagesInItem} Cloudinary images`);
    }
    
    // Update checklist items immediately for responsive UI
    setChecklistItems(prevItems => 
      prevItems.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      )
    );
    
    // Update cumulative data and trigger save
    const categoryId = currentCategory?.originalCategory?.categoryData?.category_id;
    const controlItemId = currentControlItem?.control_item_id;
    
    
    if (categoryId && controlItemId) {
      setCumulativeAuditData(prevData => {
        // Prepare images for storage (ensure all necessary data is preserved)
        const imagesToStore = (updatedItem.images || []).map(img => ({
          id: img.id,
          url: img.url,
          thumbnailUrl: img.thumbnailUrl,
          alt: img.alt,
          cloudinaryId: img.cloudinaryId || img.publicId || '',
          publicId: img.publicId || img.cloudinaryId || '',
          uploadStatus: img.uploadStatus || 'success',
          cloudinaryData: img.cloudinaryData || null,
          uploadedAt: img.uploadedAt || new Date().toISOString()
        }));
        
        const newData = {
          ...prevData,
          [categoryId]: {
            ...prevData[categoryId],
            categoryName: currentCategory.name,
            categoryTranslation: currentCategory.translation,
            controlItems: {
              ...prevData[categoryId]?.controlItems,
              [controlItemId]: {
                ...prevData[categoryId]?.controlItems?.[controlItemId],
                // Enhanced control item information
                controlItemName: currentControlItem.control_item_code,
                controlItemTranslation: currentControlItem.control_item_code_it,
                controlItemCode: currentControlItem.control_item_code,
                controlItemId: currentControlItem.control_item_id,
                // Complete original control item reference
                originalControlItem: currentControlItem,
                checks: {
                  ...prevData[categoryId]?.controlItems?.[controlItemId]?.checks,
                  [updatedItem.id]: {
                    isChecked: updatedItem.isChecked,
                    isCompleted: updatedItem.isChecked, // Mark as completed when checked
                    quantity: updatedItem.quantity || 1,
                    comment: updatedItem.comment || '',
                    productionPhase: updatedItem.productionPhase || 'Phase',
                    isFixedBeforeShipment: false, // Always false (field hidden in UI)
                    images: imagesToStore,
                    originalCheck: updatedItem.originalCheck,
                    lastUpdated: new Date().toISOString(),
                    modifiedAt: new Date().toISOString(),
                    modifiedBy: 'operator',
                    hasCloudinaryImages: cloudinaryImagesInItem > 0
                  }
                }
              }
            }
          }
        };
        
        // Update control item statistics
        const updatedControlItemData = updateControlItemStats(newData[categoryId].controlItems[controlItemId]);
        updatedControlItemData.lastInteractionAt = new Date().toISOString();
        
        // Update category statistics
        updateCategoryStats(newData[categoryId]);
        
        console.log(`📊 Control item stats updated: ${updatedControlItemData.controlItemStatus} (${updatedControlItemData.selectedChecks}/${updatedControlItemData.totalChecks})`);
        
        // Trigger debounced save with enhanced data
        console.log('🔥 IMMEDIATE DEBUG - About to call debouncedSave with data:', {
          newDataKeys: Object.keys(newData),
          categoryCount: Object.keys(newData).length,
          sampleCategory: Object.values(newData)[0]
        });
        debouncedSave(newData);
        
        // Also update parent component's checklistData state for consistency
        if (onChecklistItemChange) {
          onChecklistItemChange(categoryId, controlItemId, updatedItem.id, {
            ...updatedItem,
            images: imagesToStore
          });
        }
        
        console.log('🔍 CUMULATIVE DATA UPDATE RESULT:', {
          categoryId,
          controlItemId,
          checkId: updatedItem.id,
          newDataKeys: Object.keys(newData),
          hasControlItems: !!(newData[categoryId]?.controlItems),
          controlItemsCount: newData[categoryId]?.controlItems ? Object.keys(newData[categoryId].controlItems).length : 0,
          sampleControlItem: newData[categoryId]?.controlItems ? Object.values(newData[categoryId].controlItems)[0] : null
        });
        
        return newData;
      });
      
      // Score will be updated automatically by useEffect when cumulativeAuditData changes
    }
  }, [currentCategory, currentControlItem, debouncedSave, onChecklistItemChange]); // Removed fetchCumulativeAuditScore

  // Check if current checklist is complete with enhanced validation.
  //
  // QGS v2 · every defect row (NPD is now hidden — see ChecklistPage) must
  // carry a tri-state response before Next is allowed:
  //   OK      → clear (no further data required).
  //   REWORK  → needs an image + a comment on the row (mirrors the
  //             existing rule) so the batched flush has enough to POST.
  //   KO      → must have been synced through the KoApprovalModal
  //             (line-leader signed) so the row is already in the DB.
  //             A KO with details filled but no LL signature is NOT
  //             considered complete.
  const isCurrentChecklistComplete = useCallback(() => {
    const defectItems = checklistItems.filter(i => (i.deductionPoints || 0) !== 0);
    if (defectItems.length === 0) {
      return true;
    }
    return defectItems.every(item => {
      const rec = qgsStore.getRecord(item.id);
      const triResponse = rec?.response || null;
      if (!triResponse) return false;

      if (triResponse === 'OK') return true;
      if (triResponse === 'KO') return !!rec?.synced;
      if (triResponse === 'REWORK') {
        const hasImage = item.images && item.images.length > 0;
        const hasComment = item.comment && item.comment.trim().length > 0;
        return hasImage && hasComment;
      }
      return false;
    });
  }, [checklistItems]);

  // ---------------------------------------------------------------------
  // QGS — batched-on-Next backend write.
  //
  // Every time the operator presses Next on the checklist, any KO or
  // Rework rows on the current control item that have not been synced yet
  // are POSTed to the backend. The QGS Submission envelope is created on
  // the first flush of the audit (lazy) and reused thereafter. Every KO /
  // Rework response includes a `submission_summary` block with fresh
  // knockouts_count / reworks_count — the store hangs onto it so the
  // header pills track backend truth.
  // ---------------------------------------------------------------------
  // QGS v2 · resolves the current QGS Submission id, creating the envelope
  // lazily on the first call. Extracted so both flushQgsControlItem AND
  // the ChecklistPage KO approval modal can drive commits without either
  // needing to know about selectedForm / currentAuditResponse shape.
  const ensureQgsSubmission = useCallback(async () => {
    if (!selectedForm || !selectedForm.gate_type || !QGS_GATES.has(selectedForm.gate_type)) {
      throw new Error('Not a QGS gate — cannot create QGS submission.');
    }
    let submissionId = qgsStore.getSubmissionId();
    if (submissionId) return submissionId;

    const unitSerial = currentAuditResponse?.raw_response_json?.serialNumber
      || currentAuditResponse?.raw_response_json?.unit_serial_number
      || '';
    // Backend expects a numeric PK. selectedForm has two shapes across
    // the code paths: draft-resume sets `id` as a `form_<pk>` string and
    // carries the real PK on `audit_form_id`; the fresh selection path
    // sometimes has the numeric PK directly on `id`. Take audit_form_id
    // first, then fall back to a numeric `id`.
    const rawFormPk = selectedForm.audit_form_id ?? selectedForm.id;
    const formPk = Number(rawFormPk);
    if (!Number.isFinite(formPk)) {
      throw new Error(`Cannot resolve numeric form id from selectedForm (raw=${rawFormPk})`);
    }
    const created = await qgsApi.createSubmission({
      form: formPk,
      unit_serial: unitSerial,
      raw_response_json: {},
    });
    submissionId = created.id;
    qgsStore.setSubmissionId(submissionId);
    qgsStore.setSubmissionSummary({
      id: created.id,
      session_id: created.session_id,
      knockouts_count: created.knockouts_count || 0,
      reworks_count: created.reworks_count || 0,
      status: created.status,
    });
    return submissionId;
  }, [selectedForm, currentAuditResponse]);

  const flushQgsControlItem = useCallback(async () => {
    if (!selectedForm || !selectedForm.gate_type) return;
    if (!QGS_GATES.has(selectedForm.gate_type)) return;

    const currentCiId = currentControlItem?.control_item_id;
    if (!currentCiId) return;

    const isForCurrentCi = (r) => String(r.controlItemId) === String(currentCiId);
    const pendingKos = qgsStore.pendingKoRows().filter(isForCurrentCi);
    const pendingReworks = qgsStore.pendingReworkRows().filter(isForCurrentCi);
    if (pendingKos.length === 0 && pendingReworks.length === 0) return;

    // Lazily create the Submission envelope. serial_number rides on the
    // audit response's raw_response_json (see App.startAudit).
    const submissionId = await ensureQgsSubmission();

    // Post KOs. Missing defect_type / defect_location is fine — the row
    // is skipped and marked synced=false so it can be retried once the
    // operator fills those pickers.
    for (const ko of pendingKos) {
      const d = ko.details || {};
      if (!d.defect_type_id || !d.defect_location_id) continue;
      try {
        const saved = await qgsApi.createKnockout({
          submission: submissionId,
          check_field: Number(ko.checkId),
          defect_type: d.defect_type_id,
          defect_location: d.defect_location_id,
          initial_comment: ko.comment || d.note || '',
          initial_photo: ko.photoUrl || '',
        });
        qgsStore.markSynced(ko.checkId);
        if (saved.submission_summary) qgsStore.setSubmissionSummary(saved.submission_summary);
      } catch (e) {
        console.error(`KO POST failed for check ${ko.checkId}:`, e.message);
      }
    }

    for (const rw of pendingReworks) {
      const d = rw.details || {};
      if (!d.defect_type_id || !d.defect_location_id || !d.rework_type_id) continue;
      try {
        const saved = await qgsApi.createRework({
          submission: submissionId,
          check_field: Number(rw.checkId),
          defect_type: d.defect_type_id,
          defect_location: d.defect_location_id,
          rework_type: d.rework_type_id,
          comment: rw.comment || d.note || '',
          photo: rw.photoUrl || '',
        });
        qgsStore.markSynced(rw.checkId);
        if (saved.submission_summary) qgsStore.setSubmissionSummary(saved.submission_summary);
      } catch (e) {
        console.error(`Rework POST failed for check ${rw.checkId}:`, e.message);
      }
    }
  }, [selectedForm, currentControlItem, currentAuditResponse, ensureQgsSubmission]);

  // Navigation handlers with save-before-navigate and completion validation
  const handleNext = useCallback(async () => {
    if (isNavigating || isSaving) {
      console.log('⏳ Navigation in progress, please wait...');
      return;
    }

    // QGS v2 · Next is only enabled when every defect row on this control
    // item carries a valid response (see isCurrentChecklistComplete).  The
    // error toast explains exactly which rows are missing what.
    if (!isCurrentChecklistComplete()) {
      const defectItems = checklistItems.filter(i => (i.deductionPoints || 0) !== 0);
      const problems = [];
      defectItems.forEach(item => {
        const rec = qgsStore.getRecord(item.id);
        const tri = rec?.response || null;
        if (!tri) {
          problems.push(`"${item.label}": pick OK, REWORK, or KO`);
          return;
        }
        if (tri === 'KO' && !rec?.synced) {
          problems.push(`"${item.label}": needs Line Leader approval before Next`);
          return;
        }
        if (tri === 'REWORK') {
          const missing = [];
          if (!item.images || item.images.length === 0) missing.push('image');
          if (!item.comment || item.comment.trim().length === 0) missing.push('comment');
          if (missing.length) problems.push(`"${item.label}": missing ${missing.join(' and ')}`);
        }
      });
      if (problems.length === 0) {
        // Defensive fallback — completeness said false but nothing found.
        toast.error('Complete every check before proceeding.', { duration: 4000 });
      } else {
        toast.error(problems.join('; '), { duration: 6000 });
      }
      return;
    }
    
    console.log('Navigation: Next button clicked');
    setIsNavigating(true);
    
    try {
      // Save current data before navigating
      const saveResult = await handleNavigationSave();

      if (!saveResult || !saveResult.success) {
        // Save failed, show dialog
        handleSaveFailure(
          saveResult?.message || 'Unable to save audit data. Cannot proceed to next item.',
          () => handleNext() // Retry the same action after force save
        );
        return;
      }

      // Small delay to ensure save completes and state propagates
      await new Promise(resolve => setTimeout(resolve, 100));

      // QGS — flush any unsynced KO / Rework rows for the CURRENT control
      // item to the backend. Failures are surfaced but non-blocking: the
      // operator can keep moving, and Complete will retry the leftovers.
      try {
        await flushQgsControlItem();
      } catch (e) {
        console.error('QGS flush failed:', e);
        toast.error(`QGS sync failed: ${e.message}`, { duration: 4000 });
      }

      // Check if there are more control items in current category
      if (currentControlItemIndex < currentCategoryControlItems.length - 1) {
        console.log('Moving to next control item');
        const nextIndex = currentControlItemIndex + 1;
        setCurrentControlItemIndex(nextIndex);
        // Update category progress
        const categoryId = currentCategory?.originalCategory?.categoryData?.category_id;
        if (categoryId) {
          updateCategoryProgress(categoryId, nextIndex);
        }
      } else {
        // Current category is complete
        if (currentCategoryIndex < allCategories.length - 1) {
          console.log('Showing category transition modal');
          setIsCategoryTransitionOpen(true);
        } else {
          // Last category completed - show transition modal for summary
          console.log('All categories completed, showing summary transition modal');
          setIsCategoryTransitionOpen(true);
        }
      }
    } finally {
      setIsNavigating(false);
    }
  }, [currentControlItemIndex, currentCategoryControlItems.length, currentCategoryIndex, allCategories.length, handleNavigationSave, isNavigating, isSaving, isCurrentChecklistComplete, toast, checklistItems, flushQgsControlItem]);

  const handlePrevious = useCallback(async () => {
    if (isNavigating || isSaving) {
      console.log('⏳ Navigation in progress, please wait...');
      return;
    }
    
    setIsNavigating(true);
    
    try {
      // Save current data before navigating
      const saveResult = await handleNavigationSave();

      if (!saveResult || !saveResult.success) {
        // Save failed, show dialog
        handleSaveFailure(
          saveResult?.message || 'Unable to save audit data. Cannot proceed to previous item.',
          () => handlePrevious() // Retry the same action after force save
        );
        return;
      }
      
      // Small delay to ensure save completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (currentControlItemIndex > 0) {
        setCurrentControlItemIndex(currentControlItemIndex - 1);
      } else if (currentCategoryIndex > 0) {
        setCurrentCategoryIndex(currentCategoryIndex - 1);
        // Move to last control item of previous category
        const prevCategoryId = allCategories[currentCategoryIndex - 1].originalCategory?.categoryData?.category_id;
        if (prevCategoryId && auditFormData?.hierarchy?.control_items) {
          const prevCategoryControlItems = auditFormData.hierarchy.control_items.filter(
            controlItem => controlItem.category_id === prevCategoryId
          );
          setCurrentControlItemIndex(Math.max(0, prevCategoryControlItems.length - 1));
        }
      } else if (onPrevious) {
        onPrevious();
      }
    } finally {
      setIsNavigating(false);
    }
  }, [currentControlItemIndex, currentCategoryIndex, allCategories, auditFormData?.hierarchy?.control_items, onPrevious, handleNavigationSave, isNavigating, isSaving]);

  // Category transition with data save
  const handleContinueToNextCategory = useCallback(async () => {
    setIsCategoryTransitionOpen(false);
    
    // Save current progress before moving to next category
    await handleNavigationSave();
    
    setCurrentCategoryIndex(prev => prev + 1);
    setCurrentControlItemIndex(0);
    
    toast.info(`Moving to category ${currentCategoryIndex + 2} of ${allCategories.length}`);
  }, [handleNavigationSave, currentCategoryIndex, allCategories.length, toast]);

  const handleContinueToSummary = useCallback(async () => {
    // Validate all control items are completed
    if (!areAllControlItemsCompleted()) {
      const incompleteItems = getIncompleteControlItems();
      const incompleteCount = incompleteItems.length;
      
      toast.error(
        `Cannot proceed to summary. ${incompleteCount} control item${incompleteCount > 1 ? 's' : ''} still need${incompleteCount === 1 ? 's' : ''} to be completed. Each control item must have at least one check marked.`,
        { duration: 5000 }
      );
      
      console.log('Cannot proceed to summary. Incomplete control items:', incompleteItems);
      
      setIsCategoryTransitionOpen(false);
      return;
    }
    
    setIsCategoryTransitionOpen(false);
    
    try {
      // Save final progress before proceeding to summary
      await handleNavigationSave();
      
      // Force save all pending data
      await auditService.forceSaveAll();
      
      console.log('✅ All control items completed. Proceeding to audit summary...');
      console.log('🔍 CHECKLIST DEBUG: Cumulative data before summary:', {
        cumulativeDataKeys: Object.keys(cumulativeAuditData),
        cumulativeDataCount: Object.keys(cumulativeAuditData).length,
        sampleCategory: Object.values(cumulativeAuditData)[0],
        fullCumulativeData: cumulativeAuditData,
        currentCategory,
        currentControlItem
      });
      
      toast.info('Proceeding to audit summary...');
      
      // Refresh audit response data before navigating to ensure latest data is available
      if (onSaveProgress) {
        try {
          console.log('🔄 Refreshing audit response data before summary...');
          // Pass the actual cumulativeAuditData instead of empty object
          await onSaveProgress({ cumulativeAuditData });
          console.log('✅ Audit response data refreshed successfully');
        } catch (error) {
          console.warn('⚠️ Failed to refresh audit response data, continuing with navigation:', error);
          // Continue with navigation even if refresh fails
        }
      }
      
      // Navigate to audit summary page instead of completing immediately
      if (navigateToStep) {
        console.log('🎯 Navigating to audit summary page...');
        navigateToStep('auditSummary');
        console.log('✅ Navigation to summary initiated');
      } else {
        console.error('❌ navigateToStep callback not provided!');
        toast.error('Cannot navigate to summary - missing navigation handler');
      }
      
    } catch (error) {
      console.error('❌ Error during summary navigation:', error);
      toast.error('Failed to navigate to summary. Please try again.');
    }
  }, [handleNavigationSave, toast, areAllControlItemsCompleted, getIncompleteControlItems, navigateToStep]);

  // Force save before component unmounts or navigation
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      // Force save any pending data
      await auditService.forceSaveAll();
      
      // Show warning if there are unsaved changes
      if (saveTimeoutRef.current) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Save when app goes to background
        auditService.forceSaveAll();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // Cleanup and force save
      auditService.forceSaveAll();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Calculate progress
  const getProgress = useCallback(() => {
    const totalCategories = allCategories.length;
    const completedCategories = currentCategoryIndex;
    const currentCategoryProgress = currentCategoryControlItems.length > 0 
      ? (currentControlItemIndex + 1) / currentCategoryControlItems.length 
      : 1;
    
    return {
      completed: completedCategories,
      total: totalCategories,
      currentCategoryProgress,
      overallProgress: totalCategories > 0 ? (completedCategories + currentCategoryProgress) / totalCategories : 0
    };
  }, [allCategories.length, currentCategoryIndex, currentCategoryControlItems.length, currentControlItemIndex]);

  const progress = getProgress();

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-600 mb-4">{t('checklist.no_categories_available')}</h2>
          <p className="text-gray-500 mb-6">{t('checklist.no_categories_found')}</p>
          <Button
            onClick={() => onNavigate && onNavigate('categoryView')}
            className="px-6 py-2"
          >
            {t('btn.back')}
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state if data is still loading
  if (isLoadingFormData || !auditFormData?.hierarchy?.control_items) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!currentControlItem) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-600 mb-4">{t('checklist.no_control_items')}</h2>
          <p className="text-gray-500 mb-6">{t('checklist.no_control_items_found')}</p>
          <div className="space-y-2">
            <Button onClick={handleNext} className="px-6 py-2">
              {t('checklist.next')}
            </Button>
            <Button
              variant="outline"
              onClick={() => onNavigate && onNavigate('categoryView')}
              className="px-6 py-2"
            >
              {t('btn.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const nextCategory = currentCategoryIndex < allCategories.length - 1 
    ? allCategories[currentCategoryIndex + 1] 
    : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-40">
        <div 
          className="h-full bg-blue-600 transition-all duration-500"
          style={{ width: `${progress.overallProgress * 100}%` }}
        />
      </div>

      {/* Cloudinary Status Indicator */}
      {!cloudinaryStatus && (
        <div className="fixed top-2 right-4 bg-yellow-50 shadow-sm rounded-lg px-3 py-1 border border-yellow-200 z-30">
          <div className="text-xs text-yellow-700 flex items-center gap-1">
            <CloudOff className="h-3 w-3" />
            <span>Cloud storage offline</span>
          </div>
        </div>
      )}

      {/* Loading indicator for navigation */}
      {(isNavigating || isLoadingChecks) && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-white shadow-lg rounded-lg p-3 border border-gray-200 z-50">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-sm text-gray-700">
              {isNavigating ? t('checklist.saving_progress') : 'Loading checks...'}
            </span>
          </div>
        </div>
      )}

      {/* Enhanced Checklist Page with Cloudinary support */}
      <ChecklistPage
        key={`${currentCategory?.id}-${currentControlItem?.control_item_id}`}
        checklistItems={checklistItems}
        onItemChange={handleItemChange}
        categoryTitle={currentCategory.name}
        onNavigate={handleSafeNavigate}
        categoryTranslation={currentCategory.translation}
        controlItemTitle={currentControlItem.control_item_code}
        controlItemTitleItalian={currentControlItem.control_item_code_it}
        currentItemIndex={currentControlItemIndex + 1}
        totalItems={currentCategoryControlItems.length}
        onNext={handleNext}
        onPrevious={handlePrevious}
        selectedForm={selectedForm}
        onOpenFormDetails={() => setIsFormModalOpen(true)}
        currentCategoryIndex={currentCategoryIndex + 1}
        totalCategories={allCategories.length}
        currentControlItemIndex={currentControlItemIndex + 1}
        totalControlItems={currentCategoryControlItems.length}
        currentScore={currentScore}
        showScore={true}
        isNavigationDisabled={isNavigating || isSaving || isLoadingChecks}
        isChecklistComplete={isCurrentChecklistComplete()}
        showSummaryButton={showSummaryButton}
        onEnsureQgsSubmission={ensureQgsSubmission}
      />

      {/* Enhanced Saving indicator with Cloudinary status */}
      {isSaving && (
        <div className="fixed top-20 right-4 bg-green-50 shadow-lg rounded-lg p-3 border border-green-200 z-30">
          <div className="text-xs text-green-600">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-3 w-3 border border-green-600 border-t-transparent"></div>
              <div>
                <div className="flex items-center gap-1">
                  <span>💾 {t('checklist.saving_progress')}</span>
                  {cloudinaryStatus && <Cloud className="h-3 w-3" />}
                </div>
                {currentScore.percentage > 0 && (
                  <div className="text-green-500">Score: {currentScore.percentage}%</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Transition Modal */}
      <CategoryTransitionModal
        isOpen={isCategoryTransitionOpen}
        onClose={() => setIsCategoryTransitionOpen(false)}
        onContinue={nextCategory ? handleContinueToNextCategory : handleContinueToSummary}
        currentCategory={currentCategory}
        nextCategory={nextCategory}
        progress={progress}
        areAllCompleted={areAllControlItemsCompleted()}
        incompleteCount={getIncompleteControlItems().length}
      />

      {/* Form Details Modal */}
      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedForm?.title} - Details
            </DialogTitle>
          </DialogHeader>
          <FormDetailsModal 
            selectedForm={selectedForm}
            auditFormData={auditFormData}
            isLoadingFormData={isLoadingFormData}
            formDataError={formDataError}
          />
        </DialogContent>
      </Dialog>

      {/* Force Save Dialog */}
      <Dialog open={saveFailureDialog.open} onOpenChange={(open) => !open && setSaveFailureDialog({ open: false, message: '', pendingAction: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <X className="h-5 w-5" />
              Unable to Save Audit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              {saveFailureDialog.message}
            </div>
            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
              <strong>⚠️ Warning:</strong> Proceeding without saving may result in data loss.
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setSaveFailureDialog({ open: false, message: '', pendingAction: null })}
                className="flex-1"
              >
                Stay Here
              </Button>
              <Button
                onClick={handleForceSave}
                disabled={isSaving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Force Save'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistPageLoop;